/**
 * Camunda 7.18 Task REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/task/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  Task,
  TaskQuery,
  CountResult,
  Variables,
  VariableValue,
  CompleteTaskDto,
  TaskBpmnErrorDto,
  TaskEscalationDto,
  IdentityLink,
  TaskComment,
  Attachment,
  FormInfo,
} from '../types';

export class TaskService {
  private client: CamundaApiClient;
  private basePath = '/engine-rest/task';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Get List
  // ============================================================================

  /**
   * Get a list of tasks
   */
  async getList(query?: TaskQuery): Promise<Task[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<Task[]>(`${this.basePath}${params}`);
  }

  /**
   * Get tasks count
   */
  async getCount(query?: TaskQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/count${params}`);
  }

  /**
   * Get tasks using POST (for complex queries)
   */
  async queryList(query: TaskQuery): Promise<Task[]> {
    const { firstResult, maxResults, sortBy, sortOrder, ...body } = query;
    const params = this.buildPaginationParams({ firstResult, maxResults, sortBy, sortOrder });
    return this.client.post<Task[]>(`${this.basePath}${params}`, body);
  }

  /**
   * Get tasks count using POST
   */
  async queryCount(query: TaskQuery): Promise<CountResult> {
    return this.client.post<CountResult>(`${this.basePath}/count`, query);
  }

  // ============================================================================
  // Get Single
  // ============================================================================

  /**
   * Get task by ID
   */
  async getById(id: string): Promise<Task> {
    return this.client.get<Task>(`${this.basePath}/${id}`);
  }

  // ============================================================================
  // Task Lifecycle
  // ============================================================================

  /**
   * Complete a task
   */
  async complete(id: string, dto?: CompleteTaskDto): Promise<Variables | void> {
    return this.client.post<Variables | void>(`${this.basePath}/${id}/complete`, dto || {});
  }

  /**
   * Submit task form
   */
  async submitForm(id: string, variables?: Variables): Promise<Variables | void> {
    return this.client.post<Variables | void>(`${this.basePath}/${id}/submit-form`, { variables });
  }

  /**
   * Resolve a task
   */
  async resolve(id: string, variables?: Variables): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/resolve`, { variables });
  }

  /**
   * Delegate a task
   */
  async delegate(id: string, userId: string): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/delegate`, { userId });
  }

  // ============================================================================
  // Claim/Unclaim/Assign
  // ============================================================================

  /**
   * Claim a task
   */
  async claim(id: string, userId: string): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/claim`, { userId });
  }

  /**
   * Unclaim a task
   */
  async unclaim(id: string): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/unclaim`, {});
  }

  /**
   * Set task assignee
   */
  async setAssignee(id: string, userId: string | null): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/assignee`, { userId });
  }

  // ============================================================================
  // BPMN Error/Escalation
  // ============================================================================

  /**
   * Handle BPMN error
   */
  async handleBpmnError(id: string, dto: TaskBpmnErrorDto): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/bpmnError`, dto);
  }

  /**
   * Handle BPMN escalation
   */
  async handleBpmnEscalation(id: string, dto: TaskEscalationDto): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/bpmnEscalation`, dto);
  }

  // ============================================================================
  // Variables
  // ============================================================================

  /**
   * Get task variables
   */
  async getVariables(
    id: string,
    options?: { deserializeValues?: boolean }
  ): Promise<Variables> {
    const params = options?.deserializeValues !== undefined
      ? `?deserializeValues=${options.deserializeValues}`
      : '';
    return this.client.get<Variables>(`${this.basePath}/${id}/variables${params}`);
  }

  /**
   * Get a single task variable
   */
  async getVariable(
    id: string,
    varName: string,
    options?: { deserializeValue?: boolean }
  ): Promise<VariableValue> {
    const params = options?.deserializeValue !== undefined
      ? `?deserializeValue=${options.deserializeValue}`
      : '';
    return this.client.get<VariableValue>(`${this.basePath}/${id}/variables/${varName}${params}`);
  }

  /**
   * Get binary variable data
   */
  async getVariableBinary(id: string, varName: string): Promise<Blob> {
    return this.client.getBlob(`${this.basePath}/${id}/variables/${varName}/data`);
  }

  /**
   * Modify task variables
   */
  async modifyVariables(
    id: string,
    dto: { modifications?: Variables; deletions?: string[] }
  ): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/variables`, dto);
  }

  /**
   * Set a single task variable
   */
  async setVariable(id: string, varName: string, value: VariableValue): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/variables/${varName}`, value);
  }

  /**
   * Set binary variable data
   */
  async setVariableBinary(
    id: string,
    varName: string,
    data: Blob,
    valueType?: string
  ): Promise<void> {
    const formData = new FormData();
    formData.append('data', data);
    if (valueType) {
      formData.append('valueType', valueType);
    }
    await this.client.postFormData<void>(
      `${this.basePath}/${id}/variables/${varName}/data`,
      formData
    );
  }

  /**
   * Delete a task variable
   */
  async deleteVariable(id: string, varName: string): Promise<void> {
    await this.client.delete<void>(`${this.basePath}/${id}/variables/${varName}`);
  }

  // ============================================================================
  // Local Variables
  // ============================================================================

  /**
   * Get task local variables
   */
  async getLocalVariables(
    id: string,
    options?: { deserializeValues?: boolean }
  ): Promise<Variables> {
    const params = options?.deserializeValues !== undefined
      ? `?deserializeValues=${options.deserializeValues}`
      : '';
    return this.client.get<Variables>(`${this.basePath}/${id}/localVariables${params}`);
  }

  /**
   * Get a single task local variable
   */
  async getLocalVariable(
    id: string,
    varName: string,
    options?: { deserializeValue?: boolean }
  ): Promise<VariableValue> {
    const params = options?.deserializeValue !== undefined
      ? `?deserializeValue=${options.deserializeValue}`
      : '';
    return this.client.get<VariableValue>(`${this.basePath}/${id}/localVariables/${varName}${params}`);
  }

  /**
   * Modify task local variables
   */
  async modifyLocalVariables(
    id: string,
    dto: { modifications?: Variables; deletions?: string[] }
  ): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/localVariables`, dto);
  }

  /**
   * Set a single task local variable
   */
  async setLocalVariable(id: string, varName: string, value: VariableValue): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/localVariables/${varName}`, value);
  }

  /**
   * Delete a task local variable
   */
  async deleteLocalVariable(id: string, varName: string): Promise<void> {
    await this.client.delete<void>(`${this.basePath}/${id}/localVariables/${varName}`);
  }

  // ============================================================================
  // Form
  // ============================================================================

  /**
   * Get task form key
   */
  async getFormKey(id: string): Promise<FormInfo> {
    return this.client.get<FormInfo>(`${this.basePath}/${id}/form`);
  }

  /**
   * Get rendered task form
   */
  async getRenderedForm(id: string): Promise<string> {
    return this.client.getText(`${this.basePath}/${id}/rendered-form`);
  }

  /**
   * Get deployed task form
   */
  async getDeployedForm(id: string): Promise<string> {
    return this.client.getText(`${this.basePath}/${id}/deployed-form`);
  }

  /**
   * Get form variables
   */
  async getFormVariables(
    id: string,
    variableNames?: string[],
    deserializeValues?: boolean
  ): Promise<Variables> {
    const params = new URLSearchParams();
    if (variableNames?.length) params.append('variableNames', variableNames.join(','));
    if (deserializeValues !== undefined) params.append('deserializeValues', String(deserializeValues));
    const queryString = params.toString();
    return this.client.get<Variables>(`${this.basePath}/${id}/form-variables${queryString ? `?${queryString}` : ''}`);
  }

  // ============================================================================
  // Identity Links
  // ============================================================================

  /**
   * Get identity links
   */
  async getIdentityLinks(id: string, type?: string): Promise<IdentityLink[]> {
    const params = type ? `?type=${type}` : '';
    return this.client.get<IdentityLink[]>(`${this.basePath}/${id}/identity-links${params}`);
  }

  /**
   * Add identity link
   */
  async addIdentityLink(id: string, link: IdentityLink): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/identity-links`, link);
  }

  /**
   * Delete identity link
   */
  async deleteIdentityLink(id: string, link: IdentityLink): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/identity-links/delete`, link);
  }

  // ============================================================================
  // Comments
  // ============================================================================

  /**
   * Get task comments
   */
  async getComments(id: string): Promise<TaskComment[]> {
    return this.client.get<TaskComment[]>(`${this.basePath}/${id}/comment`);
  }

  /**
   * Get a single comment
   */
  async getComment(id: string, commentId: string): Promise<TaskComment> {
    return this.client.get<TaskComment>(`${this.basePath}/${id}/comment/${commentId}`);
  }

  /**
   * Create a comment
   */
  async createComment(id: string, message: string): Promise<TaskComment> {
    return this.client.post<TaskComment>(`${this.basePath}/${id}/comment/create`, { message });
  }

  // ============================================================================
  // Attachments
  // ============================================================================

  /**
   * Get task attachments
   */
  async getAttachments(id: string): Promise<Attachment[]> {
    return this.client.get<Attachment[]>(`${this.basePath}/${id}/attachment`);
  }

  /**
   * Get a single attachment
   */
  async getAttachment(id: string, attachmentId: string): Promise<Attachment> {
    return this.client.get<Attachment>(`${this.basePath}/${id}/attachment/${attachmentId}`);
  }

  /**
   * Get attachment content
   */
  async getAttachmentContent(id: string, attachmentId: string): Promise<Blob> {
    return this.client.getBlob(`${this.basePath}/${id}/attachment/${attachmentId}/data`);
  }

  /**
   * Create attachment
   */
  async createAttachment(
    id: string,
    attachmentName: string,
    attachmentType: string,
    options?: {
      url?: string;
      content?: Blob;
      description?: string;
    }
  ): Promise<Attachment> {
    const formData = new FormData();
    formData.append('attachment-name', attachmentName);
    formData.append('attachment-type', attachmentType);
    if (options?.url) formData.append('url', options.url);
    if (options?.content) formData.append('content', options.content);
    if (options?.description) formData.append('attachment-description', options.description);
    return this.client.postFormData<Attachment>(`${this.basePath}/${id}/attachment/create`, formData);
  }

  /**
   * Delete attachment
   */
  async deleteAttachment(id: string, attachmentId: string): Promise<void> {
    await this.client.delete<void>(`${this.basePath}/${id}/attachment/${attachmentId}`);
  }

  // ============================================================================
  // Update Task
  // ============================================================================

  /**
   * Update a task
   */
  async update(
    id: string,
    dto: {
      name?: string;
      description?: string;
      assignee?: string;
      owner?: string;
      delegationState?: 'PENDING' | 'RESOLVED';
      due?: string;
      followUp?: string;
      priority?: number;
      parentTaskId?: string;
      caseInstanceId?: string;
      tenantId?: string;
    }
  ): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}`, dto);
  }

  // ============================================================================
  // Create Task
  // ============================================================================

  /**
   * Create a new task
   */
  async create(dto: {
    id?: string;
    name?: string;
    description?: string;
    assignee?: string;
    owner?: string;
    delegationState?: 'PENDING' | 'RESOLVED';
    due?: string;
    followUp?: string;
    priority?: number;
    parentTaskId?: string;
    caseInstanceId?: string;
    tenantId?: string;
  }): Promise<void> {
    await this.client.post<void>(`${this.basePath}/create`, dto);
  }

  // ============================================================================
  // Delete Task
  // ============================================================================

  /**
   * Delete a task
   */
  async delete(id: string): Promise<void> {
    await this.client.delete<void>(`${this.basePath}/${id}`);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: TaskQuery): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else if (typeof value === 'object') {
          params.append(key, JSON.stringify(value));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  private buildPaginationParams(params: {
    firstResult?: number;
    maxResults?: number;
    sortBy?: string;
    sortOrder?: string;
  }): string {
    const urlParams = new URLSearchParams();
    if (params.firstResult !== undefined) urlParams.append('firstResult', String(params.firstResult));
    if (params.maxResults !== undefined) urlParams.append('maxResults', String(params.maxResults));
    if (params.sortBy) urlParams.append('sortBy', params.sortBy);
    if (params.sortOrder) urlParams.append('sortOrder', params.sortOrder);
    const queryString = urlParams.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let taskServiceInstance: TaskService | null = null;

export function getTaskService(): TaskService {
  if (!taskServiceInstance) {
    taskServiceInstance = new TaskService();
  }
  return taskServiceInstance;
}

export default TaskService;
